import { adminApi } from './index';

// ── Types ──────────────────────────────────────────────────────────
export interface CeleryStats {
  total: number;
  by_state: Record<string, number>;
}

export interface CeleryTask {
  id: string;
  name?: string;
  state?: string;
  args?: string;
  kwargs?: string;
  result?: string;
  received?: number;
  started?: number;
  succeeded?: number;
  failed?: number;
  retried?: number;
  runtime?: number;
  eta?: string | null;
  exception?: string;
  traceback?: string;
  worker?: string;
  live_status?: {
    worker?: string;
    bucket?: string;
    snapshot?: Record<string, unknown>;
  };
  exchange?: string;
  routing_key?: string;
  [key: string]: unknown;
}

export interface CeleryTasksResponse {
  total: number;
  tasks: CeleryTask[];
}

export interface CeleryWorker {
  [key: string]: unknown;
}

export interface CeleryCancelResponse {
  task_id: string;
  requested: boolean;
  terminate: boolean;
  signal: string;
  flower_response?: unknown;
}

export interface CeleryRerunResponse {
  original_task_id: string;
  new_task_id: string | null;
  task_name: string;
  flower_response?: unknown;
}

// ── API ────────────────────────────────────────────────────────────
export const celeryApi = {
  /** Aggregated stats (total + counts by state). */
  getStats: () => adminApi.get<CeleryStats>('/admin/celery/stats'),

  /** List tasks (newest first). */
  getTasks: (params?: { limit?: number; state?: string; taskname?: string }) =>
    adminApi.get<CeleryTasksResponse>('/admin/celery/tasks', { params }),

  /** Single task detail. */
  getTask: (taskId: string) =>
    adminApi.get<CeleryTask>(`/admin/celery/tasks/${taskId}`),

  /** Cancel/revoke a task. */
  cancelTask: (taskId: string, params?: { terminate?: boolean; signal?: string }) =>
    adminApi.post<CeleryCancelResponse>(`/admin/celery/tasks/${taskId}/cancel`, null, {
      params: {
        terminate: params?.terminate ?? true,
        signal: params?.signal ?? "SIGTERM",
      },
    }),

  /** Rerun a task with its original args/kwargs (dispatches a new task). */
  rerunTask: (taskId: string) =>
    adminApi.post<CeleryRerunResponse>(`/admin/celery/tasks/${taskId}/rerun`),

  /** Worker info. */
  getWorkers: () =>
    adminApi.get<Record<string, CeleryWorker>>('/admin/celery/workers'),
};
